import { useCallback, useRef, useState } from "react";
import { useData } from "../context/DataContext";
import { burst } from "../lib/fx";
import { sfx } from "../lib/sound";
import type { DecisionEntry } from "../types";
import { trackEvent } from "../lib/analytics";

export type Phase = "idle" | "counting" | "done";

export function useInstant<T extends { result: string }>(duration = 950) {
  const { addHistory, fastMode, prefs } = useData();
  const [phase, setPhase] = useState<Phase>("idle");
  const [outcome, setOutcome] = useState<T | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const go = useCallback((o: T, entry: Omit<DecisionEntry, "result">) => {
    clearTimeout(timer.current);
    setOutcome(o);
    setPhase("counting");
    trackEvent("decision", { kind: entry.kind, category: entry.category || undefined });
    timer.current = setTimeout(() => {
      setPhase("done");
      addHistory({ ...entry, result: o.result });
      if (prefs.sound_effects) sfx.fanfare();
      if (!fastMode) burst();
    }, fastMode ? 0 : duration);
  }, [addHistory, duration, fastMode, prefs.sound_effects]);

  return { phase, outcome, go };
}
